import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { 
  CheckCircle2, 
  Shield, 
  Clock,
  Phone,
  MessageCircle 
} from "lucide-react";


const products = [
  {
    name: "Split Hi-Wall",
    description:
      "Ideal para ambientes residenciais e comerciais, oferece excelente custo-benefício e fácil instalação.",
    image: "/images/products/split-hi-wall.png",
    features: [
      "Design moderno e compacto",
      "Operação silenciosa",
      "Filtros de alta eficiência",
      "Controle remoto com display LCD",
      "Função timer",
      "Diversas velocidades de ventilação",
    ],
  },
  {
    name: "Split Cassete",
    description:
      "Perfeito para ambientes comerciais, distribui o ar uniformemente e se integra ao forro do ambiente.",
    image: "/images/products/split-cassete.png",
    features: [
      "Distribuição de ar em 360°",
      "Design discreto",
      "Ideal para grandes ambientes",
      "Fácil manutenção",
      "Sistema de drenagem eficiente",
      "Controle preciso da temperatura",
    ],
  },
  {
    name: "Split Piso Teto",
    description:
      "Versátil, pode ser instalado no piso ou teto, ideal para ambientes comerciais que necessitam de maior capacidade.",
    image: "/images/products/split-piso-teto.png",
    features: [
      "Flexibilidade de instalação",
      "Alta capacidade de refrigeração",
      "Fluxo de ar ajustável",
      "Ideal para lojas e escritórios",
      "Operação econômica",
      "Manutenção simplificada",
    ],
  },
  {
    name: "Split Inverter",
    description:
      "Tecnologia inverter para máxima eficiência energética, conforto e economia. Ideal para quem busca climatização inteligente, silenciosa e sustentável.",
    image: "/images/products/split-inverter.png",
    features: [
      "Até 60% mais economia de energia",
      "Operação ultra silenciosa",
      "Controle preciso de temperatura",
      "Partida suave e maior durabilidade",
      "Filtro antibactéria e antiácaro",
      "Amigo do meio ambiente (gás ecológico)",
    ],
  },
];



export function Products() {
  return (
    <>
      <Helmet>
        <title>Produtos | Arcgel</title>
        <meta
          name="description"
          content="Conheça nossa linha completa de produtos para climatização: Split Hi-Wall, Cassete e Piso Teto das melhores marcas do mercado."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-32">
        <div className="container relative z-10 text-center px-4 pt-20 md:pt-28">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-6">
              <span className="text-white drop-shadow-2xl">
                Nossos
              </span>
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent ml-3">
                Produtos
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-200 mb-8 max-w-2xl mx-auto leading-relaxed">
              Linha completa de equipamentos das 
              <span className="text-cyan-300 font-semibold"> melhores marcas do mercado</span>
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/contato"
                className="group relative px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-full shadow-xl hover:shadow-cyan-500/25 transition-all duration-300 transform hover:scale-105 flex items-center gap-3"
              >
                <Phone className="w-5 h-5" />
                Solicitar Orçamento
              </Link>
              
              <a
                href="https://wa.me/5513991028221"
                target="_blank"
                rel="noopener noreferrer"
                className="group px-8 py-3 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full border border-white/30 hover:bg-green-600/20 hover:border-green-400/60 transition-all duration-300 flex items-center gap-3"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 md:py-32 relative overflow-hidden">
        <div className="container relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Linha Completa de <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Produtos</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Equipamentos de climatização das melhores marcas com tecnologia de ponta
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {products.map((product) => (
              <div
                key={product.name}
                className="group relative"
              >
                <div className="relative h-full rounded-2xl border border-slate-700/50 hover:border-cyan-400/50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:shadow-cyan-500/10 overflow-hidden">
                  {/* Imagem do produto */}
                  <div className="relative h-56 md:h-64 overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900">
                    <img
                      src={product.image}
                      alt={`${product.name} - Ar Condicionado`}
                      className="h-full w-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                  </div>

                  {/* Conteúdo */}
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-cyan-300 transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-slate-300 leading-relaxed mb-6">
                      {product.description}
                    </p>

                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-white mb-4">
                        Características Principais
                      </h4>
                      <ul className="space-y-3">
                        {product.features.map((feature) => (
                          <li
                            key={feature}
                            className="flex items-center text-slate-300"
                          >
                            <CheckCircle2 className="mr-3 h-5 w-5 text-cyan-400 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <a
                      href={`https://wa.me/5513991028221?text=${encodeURIComponent(`Olá! Gostaria de solicitar um orçamento para o produto: ${product.name}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative z-20 inline-flex items-center gap-2 px-6 py-3 bg-transparent text-white font-semibold rounded-full border border-white/40 hover:bg-green-600/30 hover:border-green-400/70 transition-all duration-300 cursor-pointer"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Solicitar Orçamento
                    </a>
                  </div>

                  {/* Efeito de brilho no hover */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-500/0 via-cyan-500/5 to-blue-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-radial from-cyan-500/20 to-transparent rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-radial from-blue-500/20 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="container relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Encontrou o produto
              <span className="block bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent">
                Ideal para Você?
              </span>
            </h2>

            <p className="text-xl md:text-2xl text-slate-300 mb-12 leading-relaxed">
              Solicite um orçamento personalizado e descubra as melhores condições de pagamento
            </p>

            <div className="flex flex-wrap justify-center gap-6 mb-12">
              {[
                { icon: CheckCircle2, text: "Orçamento Gratuito" },
                { icon: Shield, text: "Garantia Estendida" },
                { icon: Clock, text: "Entrega Rápida" }
              ].map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <div key={index} className="flex items-center gap-3 px-4 py-2 rounded-full border border-white/20">
                    <IconComponent className="w-5 h-5 text-cyan-400" />
                    <span className="text-white font-medium">{benefit.text}</span>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link
                to="/contato"
                className="group relative px-10 py-5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold text-lg rounded-full shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300 transform hover:scale-105 flex items-center gap-3 min-w-[200px] justify-center"
              >
                <Phone className="w-6 h-6" />
                Solicitar Orçamento
                
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Link>
              
              <a
                href="https://wa.me/5513991028221"
                target="_blank"
                rel="noopener noreferrer"
                className="group px-10 py-5 bg-white/10 backdrop-blur-sm text-white font-bold text-lg rounded-full border border-white/30 hover:bg-green-600/20 hover:border-green-400/60 transition-all duration-300 flex items-center gap-3 min-w-[200px] justify-center"
              >
                <MessageCircle className="w-6 h-6" />
                WhatsApp Direto
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
